<script setup lang="ts">
import { ref } from 'vue';
import TheBottomSheet from '@/components/TheBottomSheet.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import CheckCircleThin from '@/components/icons/CheckCircleThin.vue';
import DropeeTokenImage from '@/assets/images/periodic/tge/dropee-token-presale.webp';
import PresaleBGImage from '@/assets/images/presale-modal-bg.webp';
import TGEconfetti from '@/assets/images/periodic/tge/TGE-confetti.png';
import { useToaster } from '@/composables/game/toasters';
import CrossIcon from '@/components/icons/CrossIcon.vue';
import api from '@/services/api';
import { useWalletConnect } from '@/composables/game/wallet-connect';
import { useTonConnectModal } from '@townsquarelabs/ui-vue';
import { player, syncPlayer } from '@/composables/game/player';

defineProps<{
  show: boolean
}>()

defineEmits<{
  (e: 'hide'): void
}>()

const PRESALE_URL = 'https://dlp.tropee.com'

const { notify } = useToaster()
const { rawAddress, isTonWalletConnected } = useWalletConnect()
const { open: openWalletModal } = useTonConnectModal()

const isClaiming = ref(false)
const isClaimed = ref(player.value.isPresaleSubscribed ?? false)

async function claimDiscount() {
  if (!isTonWalletConnected.value || !rawAddress.value) {
    openWalletModal()
    return
  }

  isClaiming.value = true
  try {
    const res = await api.registerPresale({ walletAddress: rawAddress.value })
    await syncPlayer()
    isClaimed.value = true
    notify({ title: 'PRESALE DISCOUNT CLAIMED', type: 'success' })
  } catch (e) {
    notify({ title: 'Failed to claim discount', type: 'error' })
  } finally {
    isClaiming.value = false
  }
}

function openPresale() {
  window.open(PRESALE_URL, '_blank', 'noopener,noreferrer')
}
</script>

<template>
  <TheBottomSheet
    :show="show"
    centered
    border-container-class="presale-border overflow-visible"
    outer-content-class="overflow-visible"
    base-modal-class="overflow-visible"
    content-class="p-0 overflow-visible"
    @hide="$emit('hide')"
  >
  <template #closeButton>
      <button
        type="button"
        class="rounded-full absolute right-0 border border-[#FE44FE] bg-[#973E9B] w-5 h-5 flex items-center justify-center z-20 cursor-pointer"
        aria-label="Close presale modal"
        @click="$emit('hide')"
      >
      <CrossIcon class="text-black size-4" />
    </button>
  </template>
    <div class="w-full flex flex-col items-center relative px-8 pb-8">
      <div class="overflow-hidden rounded-[32px] w-full h-full flex-1 absolute inset-0">
        <img
          :src="PresaleBGImage"
          class="w-full h-full object-cover pointer-events-none"
          alt="PresaleDropeeBg"
        >
      </div>
      <div class="relative z-10 flex flex-col items-center w-full">
        <img
          :src="DropeeTokenImage"
          class="w-[118px] h-auto object-contain -mt-12 token-glow"
          alt="PresaleDropeeToken"
        >

        <h2 class="my-6 text-4xl leading-none font-bold uppercase -tracking-[2.2px] presale-title normal-zeros text-center">
          <span class="presale-dollar">$</span>DROPEE PRESALE
        </h2>

        <div class="discount-box bg-dark-most-bottom-layer rounded-xl px-2 py-2 mb-4 border-4 border-[#FFF246] text-center relative overflow-hidden">
          <img :src="TGEconfetti" class="absolute object-cover confetti" />
        
          <p class="text-2xl leading-7 font-semibold relative z-10">15% DISCOUNT AVAILABLE FOR YOU!</p>
        </div>
        <div class="font-medium text-white/[.87] opacity-70 text-center max-w-[280px]">
          <p class="mt-2">
            Grab $DROPEE at the best price and secure your spot early!
          </p>

          <p class="mt-2">
            Limited-time — claim your discount now & head to Presale App.
          </p>
        </div>

        <BaseButton
          v-if="!isClaimed"
          class="w-full mt-6"
          style-type="gold"
          has-shining
          :loading="isClaiming"
          @click="claimDiscount"
        >
          {{ isClaiming ? 'CLAIMING...' : 'CLAIM PRESALE DISCOUNT' }}
        </BaseButton>

        <template v-else>
          <BaseButton
            class="w-full mt-6"
            style-type="tertiary"
            icon-class="h-5 w-5"
            disabled
          >
            DISCOUNT CLAIMED
            <template #icon>
              <CheckCircleThin class="w-5 h-5 p-0.5 bg-white/[.67] text-black rounded-full" />
            </template>
          </BaseButton>

          <BaseButton
            class="w-full mt-4"
            style-type="gold"
            has-shining
            @click="openPresale"
          >
            BUY WITH DISCOUNT
          </BaseButton>
        </template>
      </div>
    </div>
  </TheBottomSheet>
</template>

<style scoped lang="scss">
.presale-title {
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.40) 0%, #FFF 50.11%, rgba(255, 255, 255, 0.40) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  -webkit-text-stroke-width: 1px;
-webkit-text-stroke-color: #000;
}

.presale-dollar {
  -webkit-text-stroke-width: 0;
}
.presale-border {
  background: linear-gradient(90deg, #FF70C1 0%, #DC41FB 50%, #635BFF 100%) !important;
}

.discount-box {
  box-shadow: 0 0 38.6px 0 rgba(230, 185, 12, 0.45);
}

.token-glow {
  filter: drop-shadow(0 0 40px rgba(200, 100, 255, 0.6));
}

.confetti {
    left: 62%;
    top: -55px;
    height: 235%;
}

</style>